import type {ReactElement} from 'react';
import {useEffect, useState, useRef} from 'react';
import {ExpandableSearchBar} from '@Components/expandable-search-bar';
import {AddAccount} from '@Components/social-media/components/add-account';
import {Checkbox} from '@Components/checkbox';
import {useAccountIdsToPublishTo, useEmphasizedPlatform, useLoadSocialAccountsForWizard, useSocialMediaWizardAccounts} from '@Components/social-media/social-media.hooks';
import type {SocialAccountListItemTooltip} from '@Components/social-media-wizard/components/social-account-list-item';
import {SocialAccountListItem} from '@Components/social-media-wizard/components/social-account-list-item';
import {Text, TextSize} from '@Components/text';
import {ClickableDiv} from '@Components/clickable-div';
import useDidUpdateEffect from '@Hooks/useDidUpdateEffect';
import {
  addConnectAccountBroadcastChannel,
  doesAccountRequireReconnection,
  getAccountConnectionResponseFromBroadcast,
  getAllPageResizeInfoForAccount,
  handleAccountConnectionResponse,
  isAccountInSearch,
  isEligibleForSocialAccountsUpsellDialog,
  isGoogleBusinessProfileAccountUnverified,
  isPostersCountValidForMultiPosting,
  isTwitterCurrentlyUnusable,
  isYouTubeCurrentlyUnusable,
  removeConnectAccountBroadcastChannel,
} from '@Libraries/social-media';
import {
  addAccountIdForPublishing,
  disableNextButton,
  enableNextButton,
  removeAccountIdFromPublishing,
  updateAccountIdsToPublishTo,
  updateEmphasizedPlatform,
  updateWizardStep,
} from '@Components/social-media-wizard/social-media-wizard-reducer';
import {SocialMediaWizardStep} from '@Components/social-media-wizard/social-media-wizard.types';
import type {SocialAccount} from '@Components/social-media/account.vo';
import {isFacebookAccount, isTikTokAccount, isPinterestAccount, MaxPostersPerPlatform} from '@Components/social-media/account.vo';
import {SocialProfileIcon, SocialProfileIconSize} from '@Components/social-media/components/social-profile-icon';
import {Button, Size, Type} from '@Components/button';
import {SocialProfileIconPlaceholder} from '@Components/social-media-wizard/components/social-profile-icon-placeholder';
import {getAccountForId, MAX_ACCOUNT_BUTTONS_TO_SHOW} from '@Libraries/social-media-wizard-library';
import type {GraphicMeta, GraphicsMetaMapType, PageHashedId, PosterHashedId} from '@Components/social-media/publish-params.types';
import {PublishingMediaType} from '@Components/social-media/publish-params.types';
import type {DesignNeedsResizeInformation, DesignSelection, PagesResizeInformationMap, PosterNeedsResizeInformation} from '@Components/social-media/social-media.types';
import {PosterResizeRequiredStatus, getReadableAccounts, SocialPlatform} from '@Components/social-media/social-media.types';
import type {SocialMediaAccountId} from '@Components/social-media/post.vo';
import {
  useAccountLevelErrorMessage,
  useAreAccountsLoading,
  useDesignsToPublish,
  useIsGoogleBusinessProfileEnabled,
  useNeedsResizeMap,
  useNumParentAccountsConnected,
  useWizardDesignsGraphicsMetaMap,
  useWizardErrorBanner,
} from '@Components/social-media-wizard/social-media-wizard.hooks';
import {WizardReauthErrorBanner} from '@Components/social-media-wizard/components/wizard-reauth-error-banner/wizard-reauth-error-banner';
import {SkeletonBrick} from '@Components/skeleton-brick';
import {MoreAccountsUpsell} from '@Components/more-accounts-upsell';
import {ErrorBox} from '@Components/error-box';
import styles from './social-accounts-list.module.scss';
import {useAppDispatch} from '@/hooks';
import {getPostableSocialMediaAccountsForUser} from '@Components/social-media/social-media-thunk';

export function SocialAccountsList(): ReactElement<any> {
  const MAX_AUTO_SELECT_NEW_CONNECTED_ACCOUNTS = 1;
  const accounts = useSocialMediaWizardAccounts();
  const accountIdsToPublishTo = useAccountIdsToPublishTo();
  const accountLevelErrorMessage = useAccountLevelErrorMessage();
  const wizardErrorBanner = useWizardErrorBanner();
  const emphasizedPlatform = useEmphasizedPlatform();
  const [areAllSelected, setAreAllSelected] = useState(false);
  const [isListLoaded, setIsListLoaded] = useState(false);
  const [accountSearchTerm, setAccountSearchTerm] = useState('');
  const dispatch = useAppDispatch();
  const numSelectedAccounts = accountIdsToPublishTo.length;
  const areAccountsLoading = useAreAccountsLoading();
  const numParentAccountsConnected = useNumParentAccountsConnected();
  const accountSearchContainerId = 'account-search-container-id';
  const posters = useDesignsToPublish();
  const designsGraphicsMetaMap = useWizardDesignsGraphicsMetaMap();
  const accountNeedsResizeMapForDesigns = useNeedsResizeMap();
  const isGoogleBusinessProfileEnabled = useIsGoogleBusinessProfileEnabled();
  const prevAccountsRef = useRef<SocialAccount[] | undefined>(undefined);

  const getAllGraphicMetas = (designsGraphicsMetaMap: GraphicsMetaMapType): GraphicMeta[] => {
    return Object.values(designsGraphicsMetaMap).flatMap((pageMeta) => Object.values(pageMeta));
  };

  const getPosterIdsFromResizeMap = (resizeInfoForDesigns: DesignNeedsResizeInformation): PosterHashedId[] => {
    return Object.keys(resizeInfoForDesigns);
  };

  const getPageIdsFromPosterResizeMap = (posterResizeInfoMap: PagesResizeInformationMap): PageHashedId[] => {
    return Object.keys(posterResizeInfoMap);
  };

  const getAvailableSocialAccounts = (): SocialAccount[] => {
    const disabledPlatforms: SocialPlatform[] = [];

    if (!isGoogleBusinessProfileEnabled) {
      disabledPlatforms.push(SocialPlatform.GOOGLE_BUSINESS_PROFILE);
    }

    return accounts.filter((account) => {
      return !disabledPlatforms.includes(account.type);
    });
  };

  const availableAccounts = getAvailableSocialAccounts();

  const getNumAccountsToPublishTo = (): number => {
    let i = 0;

    accountIdsToPublishTo.forEach((accountId) => {
      if (isAccountPublishable(accountId)) {
        i += 1;
      }
    });

    return i;
  };

  const toggleButtonByAccounts = (): void => {
    if (numSelectedAccounts > 0) {
      dispatch(enableNextButton());
    } else {
      dispatch(disableNextButton());
    }
  };

  useEffect(() => {
    toggleButtonByAccounts();
  }, []);

  useEffect(() => {
    toggleButtonByAccounts();

    return (): void => {
      dispatch(enableNextButton());
    };
  }, [numSelectedAccounts]);

  useDidUpdateEffect(() => {
    if (accounts.length === 0) {
      dispatch(disableNextButton());
    }
  }, [accounts, areAllSelected, accountSearchTerm]);

  const hasResizeMapBeenInitialized = (): boolean => {
    return Object.keys(accountNeedsResizeMapForDesigns).length !== 0;
  };

  const canPreferredAccountsBeSet = (): boolean => {
    return !isListLoaded && accounts.length > 0 && accountIdsToPublishTo.length === 0 && hasResizeMapBeenInitialized();
  };

  const selectNewConnectedAccount = (previousAccounts: SocialAccount[], currentAccounts: SocialAccount[]): void => {
    if (currentAccounts.length - previousAccounts.length > MAX_AUTO_SELECT_NEW_CONNECTED_ACCOUNTS) {
      return;
    }

    const previousIds = previousAccounts.map((account) => {
      return account.id;
    });
    const currentIds = currentAccounts.map((account) => {
      return account.id;
    });

    const newAccountId = currentIds.filter((id) => {
      return !previousIds.includes(id);
    });

    const publishableAccounts = newAccountId.filter((accountId) => {
      return isAccountPublishable(accountId);
    });

    publishableAccounts.forEach((accountId) => {
      onAccountSelected(accountId);
    });
  };

  useEffect(() => {
    if (shouldPreselectSpecificPlatform()) {
      dispatch(updateAccountIdsToPublishTo(getAccountIdsForEmphasizedPlatform()));
    } else if (canPreferredAccountsBeSet()) {
      dispatch(updateAccountIdsToPublishTo(getPreferredAccountIds()));
      setIsListLoaded(true);
    } else {
      dispatch(updateAccountIdsToPublishTo(getValidatedAccountIds()));
    }
  }, [accounts, accountNeedsResizeMapForDesigns]);

  useEffect(() => {
    if (prevAccountsRef.current) {
      selectNewConnectedAccount(prevAccountsRef.current, accounts);
    }
    prevAccountsRef.current = accounts;
  }, [accounts]);

  useLoadSocialAccountsForWizard();

  const doReloadAccounts = (broadcastEvent: MessageEvent): void => {
    const connectionResponse = getAccountConnectionResponseFromBroadcast(broadcastEvent);

    handleAccountConnectionResponse(connectionResponse, () => {
      void dispatch(getPostableSocialMediaAccountsForUser());
    });
  };

  useEffect(() => {
    const accountCreatedBroadcastChannel = addConnectAccountBroadcastChannel(doReloadAccounts);

    return (): void => {
      removeConnectAccountBroadcastChannel(accountCreatedBroadcastChannel);
    };
  }, []);

  const getAccountIdsForEmphasizedPlatform = (): SocialMediaAccountId[] => {
    const accountIdsToSelect: SocialMediaAccountId[] = [];

    availableAccounts.forEach((account) => {
      if (account.type === emphasizedPlatform && isAccountPublishable(account.id)) {
        accountIdsToSelect.push(account.id);
      }
    });

    return accountIdsToSelect;
  };

  const shouldPreselectSpecificPlatform = (): boolean => {
    return !!emphasizedPlatform;
  };

  const isAccountSelected = (account: SocialAccount): boolean => {
    return (areAllSelected || accountIdsToPublishTo.includes(account.id)) && isAccountPublishable(account.id);
  };

  const getDisabledTextForMediaType = (mediaType: PublishingMediaType, resizeInformation: PosterNeedsResizeInformation | null): string => {
    if (!resizeInformation) {
      return '';
    }

    if (resizeInformation.notSupportedCustomMessage) {
      return resizeInformation.notSupportedCustomMessage;
    }

    switch (mediaType) {
      case PublishingMediaType.IMAGE:
        return window.i18next.t('pmwjs_images_cannot_be_published');
      case PublishingMediaType.VIDEO:
        return window.i18next.t('pmwjs_videos_cannot_be_published');
      default:
        return '';
    }
  };

  const isAccountPublishable = (accountId: number): boolean => {
    const currentAccount = getAccountForId(accounts, accountId);

    if (currentAccount && doesAccountRequireReconnection(currentAccount)) {
      return false;
    }

    if (currentAccount && currentAccount.type === SocialPlatform.FACEBOOK_GROUP) {
      return false;
    }

    if (currentAccount && isFacebookPersonalProfile(currentAccount)) {
      return false;
    }

    if (currentAccount && (isTwitterCurrentlyUnusable(currentAccount) || isYouTubeCurrentlyUnusable(currentAccount) || isGoogleBusinessProfileAccountUnverified(currentAccount))) {
      return false;
    }

    if (currentAccount && !isPostersCountValidForMultiPosting(posters, currentAccount)) {
      return false;
    }

    if (currentAccount && isFacebookAccount(currentAccount) && !validateMultiPostingMedia()) {
      return false;
    }

    if (currentAccount && isTikTokAccount(currentAccount) && !validateMultiPostingMedia()) {
      return false;
    }

    if (currentAccount && isPinterestAccount(currentAccount) && !validateMultiPostingMedia()) {
      return false;
    }

    if (getUnsupportedDesigns(accountId).length > 0) {
      return false;
    }

    return true;
  };

  const validateMultiPostingMedia = (): boolean => {
    const graphicMetas = getAllGraphicMetas(designsGraphicsMetaMap);

    if (graphicMetas.length === 1) {
      return true;
    }

    for (const graphicMeta of graphicMetas) {
      if (graphicMeta.isVideo) {
        return false;
      }
    }

    return true;
  };

  const getUnsupportedDesigns = (accountID: SocialMediaAccountId): DesignSelection[] => {
    if (!accountNeedsResizeMapForDesigns[accountID]) {
      return [];
    }

    const unsupportedDesigns: DesignSelection[] = [];
    const resizeInfoForDesigns = accountNeedsResizeMapForDesigns[accountID];

    for (const posterID of getPosterIdsFromResizeMap(resizeInfoForDesigns)) {
      for (const pageID of getPageIdsFromPosterResizeMap(resizeInfoForDesigns[posterID])) {
        const designResizeInformation = resizeInfoForDesigns[posterID][pageID];

        if (designResizeInformation.needsResizing === PosterResizeRequiredStatus.NOT_SUPPORTED) {
          unsupportedDesigns.push({
            posterHashedId: posterID,
            pageHashedId: pageID,
          });
        }
      }
    }

    return unsupportedDesigns;
  };

  const isResizeNeededForAnyPoster = (accountID: number): boolean => {
    if (!accountNeedsResizeMapForDesigns[accountID]) {
      return false;
    }

    return doesAnyPosterHaveResizeStatus(PosterResizeRequiredStatus.RESIZE_NEEDED, accountID);
  };

  const isBackendOptimisationNeededForAnyPoster = (accountID: number): boolean => {
    if (!accountNeedsResizeMapForDesigns[accountID]) {
      return false;
    }

    return doesAnyPosterHaveResizeStatus(PosterResizeRequiredStatus.BACKEND_OPTIMIZATION_NEEDED, accountID);
  };

  const doesAnyPosterHaveResizeStatus = (resizeStatusToMatch: PosterResizeRequiredStatus, accountId: SocialMediaAccountId): boolean => {
    const allPageResizeInfo = getAllPageResizeInfoForAccount(accountNeedsResizeMapForDesigns, accountId);
    return allPageResizeInfo.some((pageResizeInfo) => pageResizeInfo.needsResizing === resizeStatusToMatch);
  };

  const getFirstUnsupportedResizeInfoForAccount = (accountId: SocialMediaAccountId): PosterNeedsResizeInformation | null => {
    const unsupportedDesigns = getUnsupportedDesigns(accountId);

    if (unsupportedDesigns.length === 0) {
      return null;
    }

    const {posterHashedId, pageHashedId} = unsupportedDesigns[0];
    return accountNeedsResizeMapForDesigns[accountId][posterHashedId][pageHashedId];
  };

  const isFacebookPersonalProfile = (account: SocialAccount): boolean => {
    return account.type === SocialPlatform.FACEBOOK_PROFILE;
  };

  const isGraphicsMetaLoading = (): boolean => {
    if (Object.keys(designsGraphicsMetaMap).length === 0) {
      return true;
    }

    const graphicMetas = getAllGraphicMetas(designsGraphicsMetaMap);
    return graphicMetas.some((graphicMeta: GraphicMeta) => graphicMeta.graphicURL === '');
  };

  const getMultiPostingPlatformLimitWarning = (platformLimit: string, readableAccountPlatform: string): string => {
    return platformLimit === '1'
      ? window.i18next.t('pmwjs_posters_exceeded_multi_posting_info', {account: readableAccountPlatform})
      : window.i18next.t('pmwjs_posters_exceeded_multi_posting_info_plural', {
          account: readableAccountPlatform,
          limit: platformLimit,
        });
  };

  const getTooltipIfNeeded = (account: SocialAccount): SocialAccountListItemTooltip | undefined => {
    let tooltip: SocialAccountListItemTooltip | undefined;
    const unsupportedPosters = getUnsupportedDesigns(account.id);

    const firstUnsupportedResizeInfo = getFirstUnsupportedResizeInfoForAccount(account.id);

    if (isFacebookPersonalProfile(account)) {
      tooltip = {
        icon: 'icon-exclamation-triangle',
        text: window.i18next.t('pmwjs_facebook_profile_unpostable'),
      };
    } else if (account.type === SocialPlatform.FACEBOOK_GROUP) {
      tooltip = {
        icon: 'icon-exclamation-triangle',
        text: window.i18next.t('pmwjs_facebook_group_unpostable'),
      };
    } else if (isTwitterCurrentlyUnusable(account)) {
      tooltip = {
        icon: 'icon-exclamation-triangle',
        text: window.i18next.t('pmwjs_twitter_suspension_message'),
      };
    } else if (isYouTubeCurrentlyUnusable(account)) {
      tooltip = {
        icon: 'icon-exclamation-triangle',
        text: window.i18next.t('pmwjs_youtube_outage_message'),
      };
    } else if (isGoogleBusinessProfileAccountUnverified(account)) {
      tooltip = {
        icon: 'icon-exclamation-triangle',
        text: window.i18next.t('pmwjs_google_business_profile_unverified_message'),
      };
    } else if (!isPostersCountValidForMultiPosting(posters, account)) {
      const readableAccountPlatform = getReadableAccounts(account.type);
      const platformLimit = `${MaxPostersPerPlatform[account.type] ?? ''}`;
      const text = getMultiPostingPlatformLimitWarning(platformLimit, readableAccountPlatform);

      tooltip = {
        icon: 'icon-info',
        text,
        classes: styles.infoIcon,
      };
    } else if (isPinterestAccount(account) && !validateMultiPostingMedia()) {
      tooltip = {
        icon: 'icon-info',
        text: window.i18next.t('pmwjs_pinterest_multi_posting_unsupported_videos'),
        classes: styles.infoIcon,
      };
    } else if (isFacebookAccount(account) && !validateMultiPostingMedia()) {
      tooltip = {
        icon: 'icon-info',
        text: window.i18next.t('pmwjs_facebook_multi_posting_unsupported_videos'),
        classes: styles.infoIcon,
      };
    } else if (isTikTokAccount(account) && !validateMultiPostingMedia()) {
      tooltip = {
        icon: 'icon-info',
        text: window.i18next.t('pmwjs_tiktok_multi_posting_unsupported_videos'),
        classes: styles.infoIcon,
      };
    } else if (unsupportedPosters.length > 0) {
      if (isGraphicsMetaLoading()) {
        return undefined;
      }
      const {posterHashedId, pageHashedId} = unsupportedPosters[0];
      const mediaType = designsGraphicsMetaMap[posterHashedId][pageHashedId].isVideo ? PublishingMediaType.VIDEO : PublishingMediaType.IMAGE;
      tooltip = {
        icon: 'icon-info',
        text: getDisabledTextForMediaType(mediaType, firstUnsupportedResizeInfo),
      };
    } else if (isResizeNeededForAnyPoster(account.id)) {
      tooltip = {
        icon: 'icon-resize',
        text: window.i18next.t('pmwjs_your_design_will_be_resized'),
      };
    } else if (isBackendOptimisationNeededForAnyPoster(account.id)) {
      tooltip = {
        icon: 'icon-resize',
        text: window.i18next.t('pmwjs_your_design_will_be_processed'),
      };
    }

    return tooltip;
  };

  const getListItems = (): ReactElement<any>[] => {
    const accountCheckItems: ReactElement<any>[] = [];
    let tooltip: SocialAccountListItemTooltip | undefined;

    availableAccounts.forEach((account) => {
      tooltip = getTooltipIfNeeded(account);

      accountCheckItems.push(
        <li key={`list-${account.id}`} className={`spacing-m-b-1 _unpadded ${!isAccountInSearch(accountSearchTerm, account.name) ? '_hidden' : ''}`}>
          <SocialAccountListItem
            key={`social-account-item-${account.id}`}
            account={account}
            tooltip={tooltip}
            isDisabled={!isAccountPublishable(account.id)}
            isSelected={isAccountSelected(account)}
            onCheck={onAccountChecked}
            onUncheck={onAccountUnchecked}
          />
        </li>
      );
    });

    return accountCheckItems;
  };

  const moveToConnectAccountsStep = (): void => {
    dispatch(updateWizardStep(SocialMediaWizardStep.CONNECT_ACCOUNTS));
  };

  const onAccountChecked = (idAccount: number): void => {
    onAccountSelected(idAccount);
    onAccountSelectionUpdated();
  };

  const onAccountUnchecked = (idAccount: number): void => {
    onAccountUnselected(idAccount);
    onAccountSelectionUpdated();
  };

  const onAccountSelectionUpdated = (): void => {
    dispatch(updateEmphasizedPlatform(undefined));
  };

  const onAccountSelected = (idAccount: number): void => {
    dispatch(addAccountIdForPublishing(idAccount));
  };

  const onAccountUnselected = (idAccount: number): void => {
    dispatch(removeAccountIdFromPublishing(idAccount));
  };

  const onSelectAll = (): void => {
    const newIsSelected = !areAllSelected;

    setAreAllSelected(newIsSelected);
    if (newIsSelected) {
      dispatch(updateAccountIdsToPublishTo(getAllSelectableAccountIds()));
    } else {
      dispatch(updateAccountIdsToPublishTo([]));
    }

    onAccountSelectionUpdated();
  };

  const onDeselectAll = (): void => {
    setAreAllSelected(false);

    dispatch(updateAccountIdsToPublishTo([]));
    onAccountSelectionUpdated();
  };

  const getValidatedAccountIds = (): SocialMediaAccountId[] => {
    const validatedAccountIds: SocialMediaAccountId[] = [];

    accountIdsToPublishTo.forEach((accountId) => {
      if (isAccountPublishable(accountId)) {
        validatedAccountIds.push(accountId);
      }
    });

    return validatedAccountIds;
  };

  const getPreferredAccountIds = (): SocialMediaAccountId[] => {
    const preferredAccountIds: SocialMediaAccountId[] = [];

    availableAccounts.forEach((account) => {
      if (account.isPreferred && isAccountPublishable(account.id)) {
        preferredAccountIds.push(account.id);
      }
    });

    return preferredAccountIds;
  };

  const getAllSelectableAccountIds = (): number[] => {
    const accountIds: number[] = [];

    availableAccounts.forEach((account) => {
      if (isAccountPublishable(account.id)) {
        accountIds.push(account.id);
      }
    });

    return accountIds;
  };

  const getSelectAllOption = (): ReactElement<any> => {
    return (
      <li className="spacing-m-b-1 _unpadded">
        <ClickableDiv className={`_cursor-pointer flex-h-row spacing-p-2 radius-4 ${styles.listItem} ${areAllSelected ? styles.selected : ''} `} onClick={onSelectAll}>
          <Checkbox className="spacing-m-r-5" isChecked={areAllSelected} />
          <Text val={window.i18next.t('pmwjs_select_all')} size={TextSize.XSMALL} />
        </ClickableDiv>
      </li>
    );
  };

  const onSearchInputChange = (searchTerm: string): void => {
    setAccountSearchTerm(searchTerm);
  };

  const getSelectedAccountButtons = (): ReactElement<any>[] => {
    const accountButtons: ReactElement<any>[] = [];
    let hasAppendedPlaceholder = false;
    let numButtonsHidden = 0;
    availableAccounts.forEach((account) => {
      if (accountIdsToPublishTo.includes(account.id) && isAccountPublishable(account.id)) {
        if (accountButtons.length >= MAX_ACCOUNT_BUTTONS_TO_SHOW) {
          numButtonsHidden += 1;
          hasAppendedPlaceholder = true;
        } else {
          accountButtons.push(
            <SocialProfileIcon
              key={`social-profile-icon-${account.id}`}
              className="spacing-m-r-2"
              account={account}
              size={SocialProfileIconSize.LARGE}
              onClick={(): void => {
                onAccountUnselected(account.id);
              }}
            />
          );
        }
      }
    });

    if (hasAppendedPlaceholder) {
      accountButtons.push(<SocialProfileIconPlaceholder countToShow={numButtonsHidden} size={SocialProfileIconSize.LARGE} />);
    }

    return accountButtons;
  };

  const getSelectedAccountsElements = (): ReactElement<any> | null => {
    let accountButtonsElements = null;
    const numAccountsToPublishTo = getNumAccountsToPublishTo();

    if (numAccountsToPublishTo > 0) {
      const deselectText = numAccountsToPublishTo === 1 ? window.i18next.t('pmwjs_deselect') : window.i18next.t('pmwjs_deselect_all');

      accountButtonsElements = (
        <>
          <Text val={`${window.i18next.t('pmwjs_selected')} (${numAccountsToPublishTo})`} size={TextSize.XSMALL} bold className={`spacing-m-b-2 ${styles.textElement}`} />
          <div className="flex-h-row flex-content-between">
            <div className={`flex-h-row ${styles.accountButtons}`}>{getSelectedAccountButtons()}</div>
            <Button customClasses={styles.deselectAllBtn} text={deselectText} type={Type.GHOST} size={Size.SMALL} onClick={onDeselectAll} />
          </div>
        </>
      );
    }

    return <div className={`${styles.accountButtonsContainer} ${numAccountsToPublishTo === 0 ? styles.noAccounts : ''}`}>{accountButtonsElements}</div>;
  };

  const getLoadingAccounts = (): ReactElement<any>[] => {
    const loadingAccounts = [];
    const nLoadingAccounts = 4;

    for (let i = 0; i < nLoadingAccounts; i++) {
      loadingAccounts.push(
        <div className={`flex-row-align-center ${styles.loadingAccountContainer}`} key={`skeleton-loader-${i}`}>
          <SkeletonBrick height={16} width={16} className={`spacing-m-r-4 ${styles.loadingAccountIndex}`} />
          <SkeletonBrick height={32} width={32} className={`spacing-m-r-4 ${styles.loadingAccountPicture}`} />
          <SkeletonBrick height={16} className={`spacing-m-r-4 ${styles.loadingAccountDetails}`} />
        </div>
      );
    }

    return loadingAccounts;
  };

  const getAddedAcccountsLabel = (): ReactElement<any> | null => {
    const numAccountsToPublishTo = getNumAccountsToPublishTo();

    if (numAccountsToPublishTo > 0) {
      return <Text val={window.i18next.t('pmwjs_added_accounts')} size={TextSize.XSMALL} bold className={`spacing-m-b-1 ${styles.textElement}`} />;
    }

    return null;
  };

  const onSearchFocus = (): void => {
    document.getElementById(accountSearchContainerId)?.scrollIntoView({behavior: 'smooth', block: 'start'});
  };

  const getLoadingState = (): ReactElement<any> => {
    return (
      <>
        <SkeletonBrick height={40} className={`${styles.skeletonHeader} spacing-m-b-3`} />
        {getLoadingAccounts()}
      </>
    );
  };

  const getAddAccountButton = (): ReactElement<any> => {
    if (isEligibleForSocialAccountsUpsellDialog(numParentAccountsConnected)) {
      return (
        <div className="spacing-m-t-2">
          <MoreAccountsUpsell />
        </div>
      );
    }

    return (
      <div className={`spacing-p-t-2 spacing-p-b-2 ${styles.addAccountWrapper}`}>
        <AddAccount onCustomAdd={moveToConnectAccountsStep} />
      </div>
    );
  };

  if (areAccountsLoading) {
    return getLoadingState();
  }

  const getSearchBarElement = (): ReactElement<any> => {
    return (
      <div id={accountSearchContainerId}>
        <ExpandableSearchBar
          onFocus={onSearchFocus}
          onInputChange={onSearchInputChange}
          classes="spacing-m-b-3"
          placeholder={window.i18next.t('pmwjs_search_accounts_ellipsis')}
          isExpanded
        />
      </div>
    );
  };

  const getErrorMessageBox = (): ReactElement<any> | null => {
    if (accountLevelErrorMessage.title === '' && accountLevelErrorMessage.description === '') {
      return null;
    }

    return (
      <div className="spacing-m-b-4">
        <ErrorBox title={accountLevelErrorMessage.title} description={accountLevelErrorMessage.description} />
      </div>
    );
  };

  const getReauthErrorBanner = (): ReactElement | null => {
    if (!wizardErrorBanner) {
      return null;
    }

    return <WizardReauthErrorBanner banner={wizardErrorBanner} />;
  };

  return (
    <div>
      {getReauthErrorBanner()}
      {getErrorMessageBox()}
      {getSelectedAccountsElements()}
      {getAddedAcccountsLabel()}
      {getSearchBarElement()}
      {getAddAccountButton()}
      <ul id="select-accounts-onboarding-container" className="spacing-m-t-3">
        {getSelectAllOption()}
        {getListItems()}
      </ul>
    </div>
  );
}
